import {
    type ComponentType,
    type Context,
    type FunctionComponent,
    type PropsWithChildren,
    type ReactNode,
    createElement,
    Suspense,
    useCallback,
    useEffect,
    useReducer,
    useState,
} from "react";

import { type IExtensionFeed } from "./extensibility/extensionFeed";
import { type IExtension, type InstallFailedInfo, ExtensionManager } from "./extensibility/extensionManager";
import { type WeaklyTypedServiceDefinition, ServiceContainer } from "./modularity/serviceContainer";
import { type ISettingsStore, SettingsStore, SettingsStoreIdentity } from "./services/settingsStore";
import { type IRootComponentService, type ShellServiceOptions, MakeShellServiceDefinition, RootComponentServiceIdentity } from "./services/shellService";
import { type ThemeMode, ThemeModeSettingDescriptor, ThemeServiceDefinition } from "./services/themeService";

import {
    Body1,
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    List,
    ListItem,
    makeStyles,
    Spinner,
    tokens,
} from "@fluentui/react-components";
import { CheckmarkCircleRegular, ErrorCircleRegular, InfoRegular, WarningRegular } from "@fluentui/react-icons";
import { createRoot } from "react-dom/client";

import { Deferred } from "core/Misc/deferred";
import { Logger } from "core/Misc/logger";
import { type ToastHandle, type ToastOptions, ToastProvider } from "shared-ui-components/fluent/primitives/toast";
import { type DialogOptions, type IDialogService, DialogServiceIdentity } from "./services/dialogService";
import { type IToastService, ToastServiceIdentity } from "./services/toastService";
import { Theme } from "./components/theme";
import { DialogContext } from "./contexts/dialogContext";
import { ExtensionManagerContext } from "./contexts/extensionManagerContext";
import { SettingsStoreContext } from "./contexts/settingsContext";
import { TeachingMomentsContext } from "./contexts/teachingMomentsContext";
import { type IReactContextService, type ReactContextHandle, ReactContextServiceIdentity } from "./services/reactContextService";
import { ThemeSelectorServiceDefinition } from "./services/themeSelectorService";

const useStyles = makeStyles({
    app: {
        colorScheme: "light dark",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor: tokens.colorTransparentBackground,
    },
    spinner: {
        flexGrow: 1,
        animationDuration: "1s",
        animationName: {
            from: { opacity: 0 },
            to: { opacity: 1 },
        },
    },
    extensionErrorTitleDiv: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
    },
    extensionErrorIcon: {
        color: tokens.colorPaletteRedForeground1,
    },
    dialogTitle: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
    },
    dialogIconSuccess: {
        color: tokens.colorStatusSuccessForeground1,
    },
    dialogIconError: {
        color: tokens.colorStatusDangerForeground1,
    },
    dialogIconWarning: {
        color: tokens.colorStatusWarningForeground1,
    },
    dialogIconInfo: {
        color: tokens.colorBrandForeground1,
    },
});

type ReactContextEntry = {
    provider: Context<any>["Provider"];
    value: unknown;
    order: number;
};

type ReactContextAction =
    { type: "add"; entry: ReactContextEntry } | { type: "remove"; provider: Context<any>["Provider"] } | { type: "update"; provider: Context<any>["Provider"]; value: unknown };

type DialogQueueAction = { type: "enqueue"; options: DialogOptions } | { type: "dequeue" };

const ReactContextsWrapper: FunctionComponent<PropsWithChildren<{ contexts: readonly Readonly<ReactContextEntry>[] }>> = ({ contexts, children }) => {
    return <>{contexts.reduceRight<ReactNode>((acc, entry) => createElement(entry.provider, { value: entry.value }, acc), children)}</>;
};

export type ModularToolOptions = {
    /**
     * The namespace for the tool, used for scoping persisted settings and other storage.
     */
    namespace: string;

    /**
     * The container element where the tool will be rendered.
     */
    containerElement: HTMLElement;

    /**
     * The service definitions to be registered with the tool.
     */
    serviceDefinitions: readonly WeaklyTypedServiceDefinition[];

    /**
     * The theme mode to use. If not specified, the default is "system", which uses the system/browser preference, and the last used mode is persisted.
     */
    themeMode?: ThemeMode;

    /**
     * Whether to show the theme selector in the toolbar. Default is true.
     */
    showThemeSelector?: boolean;

    /**
     * The extension feeds that provide optional extensions the user can install.
     */
    extensionFeeds?: readonly IExtensionFeed[];

    /**
     * An optional parent ServiceContainer. Dependencies not found in the tool's own container
     * will be resolved from this parent.
     */
    parentContainer?: ServiceContainer;

    /**
     * When true, all teaching moments are disabled and will not be shown.
     */
    disableTeachingMoments?: boolean;
} & ShellServiceOptions;

/**
 * Creates a modular tool with a base set of common tool services, including the toolbar/side pane basic UI layout.
 * @param options The options for the tool.
 * @returns A token that can be used to dispose of the tool.
 */
export function MakeModularTool(options: ModularToolOptions) {
    const { namespace, containerElement, serviceDefinitions, themeMode, showThemeSelector = true, extensionFeeds = [], parentContainer, disableTeachingMoments = false } = options;

    const teachingMomentsContextValue = { disabled: disableTeachingMoments };

    // Create the settings store immediately as it will be exposed to services and through React context.
    const settingsStore = new SettingsStore(namespace);

    // If a theme mode is provided, just write the setting so it is the active theme.
    if (themeMode) {
        settingsStore.writeSetting(ThemeModeSettingDescriptor, themeMode);
    }

    // This deferred resolves once the React effect cleanup (which disposes the ServiceContainer) is complete.
    const disposeDeferred = new Deferred<void>();

    const modularToolRootComponent: FunctionComponent = () => {
        const classes = useStyles();
        const [requiredExtensions, setRequiredExtensions] = useState<string[]>();
        const [requiredExtensionsDeferred, setRequiredExtensionsDeferred] = useState<Deferred<boolean>>();
        const [extensionInstallError, setExtensionInstallError] = useState<InstallFailedInfo>();

        const [toastHandle, setToastHandle] = useState<ToastHandle | null>(null);
        const [toastQueue, setToastQueue] = useState<{ message: string; options?: ToastOptions }[]>([]);

        useEffect(() => {
            if (toastHandle && toastQueue.length > 0) {
                for (const { message, options } of toastQueue) {
                    toastHandle.showToast(message, options);
                }
                setToastQueue([]);
            }
        }, [toastHandle, toastQueue]);

        // Queue of dialogs to display. We show one at a time (the one at the head of the queue).
        const [dialogQueue, dispatchDialogQueue] = useReducer((state: readonly DialogOptions[], action: DialogQueueAction): readonly DialogOptions[] => {
            switch (action.type) {
                case "enqueue":
                    return [...state, action.options];
                case "dequeue":
                    return state.slice(1);
            }
        }, []);

        const showDialog = useCallback((dialogOptions: DialogOptions) => {
            dispatchDialogQueue({ type: "enqueue", options: dialogOptions });
        }, []);

        const onDismissDialog = useCallback(() => {
            dispatchDialogQueue({ type: "dequeue" });
        }, []);

        const [rootComponentService, setRootComponentService] = useState<IRootComponentService>();

        const [contexts, updateContexts] = useReducer(
            (state: ReactContextEntry[], action: ReactContextAction): ReactContextEntry[] => {
                switch (action.type) {
                    case "add":
                        return [...state, action.entry].sort((a, b) => a.order - b.order);
                    case "remove":
                        return state.filter((e) => e.provider !== action.provider);
                    case "update":
                        return state.map((e) => (e.provider === action.provider ? { ...e, value: action.value } : e));
                }
            },
            [
                // Static contexts seeded into the wrapper so they don't add JSX nesting below.
                // Negative orders keep them as the outermost providers (lowest order = outermost via reduceRight).
                { provider: TeachingMomentsContext.Provider, value: teachingMomentsContextValue, order: -2 },
                { provider: SettingsStoreContext.Provider, value: settingsStore, order: -1 },
                { provider: ExtensionManagerContext.Provider, value: undefined, order: 0 },
            ]
        );

        // This is the main async initialization.
        useEffect(() => {
            const initializeExtensionManagerAsync = async () => {
                const serviceContainer = new ServiceContainer("ModularToolContainer", parentContainer);

                // Expose the settings store as a service so other services can read/write settings.
                serviceContainer.addService<[ISettingsStore], []>({
                    friendlyName: "Settings Store",
                    produces: [SettingsStoreIdentity],
                    factory: () => settingsStore,
                });

                // Expose the react context service so other services can add React context providers.
                serviceContainer.addService<[IReactContextService], []>({
                    friendlyName: "React Context Service",
                    produces: [ReactContextServiceIdentity],
                    factory: (): IReactContextService => ({
                        addContext<T>(provider: Context<T>["Provider"], initialValue: T, options?: { order?: number }): ReactContextHandle<T> {
                            updateContexts({ type: "add", entry: { provider: provider, value: initialValue, order: options?.order ?? 0 } });
                            return {
                                updateValue: (newValue: T) => {
                                    updateContexts({ type: "update", provider: provider, value: newValue });
                                },
                                dispose: () => {
                                    updateContexts({ type: "remove", provider: provider });
                                },
                            };
                        },
                    }),
                });

                // Expose the toast service so non-React code (e.g. Observable callbacks) can show toasts.
                serviceContainer.addService<[IToastService], []>({
                    friendlyName: "Toast Service",
                    produces: [ToastServiceIdentity],
                    factory: (): IToastService => ({
                        showToast: (message, options) => {
                            setToastQueue((prev) => [...prev, { message, options }]);
                        },
                    }),
                });

                // Expose the dialog service so non-React code (e.g. Observable callbacks) can show dialogs.
                serviceContainer.addService<[IDialogService], []>({
                    friendlyName: "Dialog Service",
                    produces: [DialogServiceIdentity],
                    factory: (): IDialogService => ({ showDialog }),
                });

                // Register the shell service (top level toolbar/side pane UI layout).
                serviceContainer.addService(MakeShellServiceDefinition(options));

                // Register a service that simply consumes the services we need before first render.
                serviceContainer.addService<[], [IRootComponentService]>({
                    friendlyName: "Service Bootstrapper",
                    consumes: [RootComponentServiceIdentity],
                    factory: (rootComponent) => {
                        // Use function syntax for the state setter since the root component may be a function component.
                        setRootComponentService(() => rootComponent);
                        return {
                            dispose: () => setRootComponentService(undefined),
                        };
                    },
                });

                // Register the theme service (exposes the current theme to other services).
                serviceContainer.addService(ThemeServiceDefinition);

                // Register the theme selector service (for selecting the theme) if theming is configured.
                if (showThemeSelector) {
                    serviceContainer.addService(ThemeSelectorServiceDefinition);
                }

                // Register the extension list service (for browsing/installing extensions) if extension feeds are provided.
                if (extensionFeeds.length > 0) {
                    const { ExtensionListServiceDefinition } = await import("./services/extensionsListService");
                    serviceContainer.addService(ExtensionListServiceDefinition);
                }

                // Register all external services (that make up a unique tool).
                serviceContainer.addServices(...serviceDefinitions);

                // Create the extension manager, passing along the registry for runtime changes to the registered services.
                const extensionManager = await ExtensionManager.CreateAsync(namespace, serviceContainer, extensionFeeds, setExtensionInstallError);

                // Check query params for required extensions. This lets users share links with sets of extensions.
                const queryParams = new URLSearchParams(window.location.search);
                const requiredExtensions = queryParams.getAll("babylon.requiredExtension");
                const uninstalledExtensions: IExtension[] = [];
                for (const requiredExtension of requiredExtensions) {
                    // These could possibly be parallelized to speed things up, but it's more complex so let's wait and see if we need it.
                    // eslint-disable-next-line no-await-in-loop
                    const query = await extensionManager.queryExtensionsAsync(requiredExtension);
                    // eslint-disable-next-line no-await-in-loop
                    const extensions = await query.getExtensionsAsync(0, query.totalCount);
                    for (const extension of extensions) {
                        if (!extension.isInstalled) {
                            uninstalledExtensions.push(extension);
                        }
                    }
                }

                // Check if any required extensions are uninstalled or disabled. If so, show a dialog to the user.
                if (uninstalledExtensions.length > 0) {
                    setRequiredExtensions(uninstalledExtensions.map((extension) => extension.metadata.name));
                    const deferred = new Deferred<boolean>();
                    setRequiredExtensionsDeferred(deferred);
                    if (await deferred.promise) {
                        for (const extension of uninstalledExtensions) {
                            // This could possibly be parallelized to speed things up, but it's more complex so let's wait and see if we need it.
                            // eslint-disable-next-line no-await-in-loop
                            await extension.installAsync();
                        }
                    }
                }

                // Set the contexts.
                updateContexts({ type: "update", provider: ExtensionManagerContext.Provider, value: { extensionManager } });

                return () => {
                    extensionManager.dispose();
                    serviceContainer.dispose();
                    serviceContainer.dispose();
                };
            };

            const disposePromise = initializeExtensionManagerAsync();

            return () => {
                disposePromise
                    // eslint-disable-next-line github/no-then
                    .then((dispose) => dispose())
                    // eslint-disable-next-line github/no-then
                    .catch((error) => {
                        Logger.Error(`Failed to dispose of the modular tool: ${error}`);
                    })
                    .finally(() => disposeDeferred.resolve());
            };
        }, []);

        const onAcceptRequiredExtensions = useCallback(() => {
            setRequiredExtensions(undefined);
            requiredExtensionsDeferred?.resolve(true);
        }, [setRequiredExtensions, requiredExtensionsDeferred]);

        const onRejectRequiredExtensions = useCallback(() => {
            setRequiredExtensions(undefined);
            requiredExtensionsDeferred?.resolve(false);
        }, [setRequiredExtensions, requiredExtensionsDeferred]);

        const onAcknowledgedExtensionInstallError = useCallback(() => {
            setExtensionInstallError(undefined);
        }, [setExtensionInstallError]);

        // The dialog at the head of the queue, if any, is the one currently being displayed.
        const currentDialog = dialogQueue[0];
        const currentDialogIcon = (() => {
            switch (currentDialog?.intent) {
                case "success":
                    return <CheckmarkCircleRegular className={classes.dialogIconSuccess} />;
                case "error":
                    return <ErrorCircleRegular className={classes.dialogIconError} />;
                case "warning":
                    return <WarningRegular className={classes.dialogIconWarning} />;
                case "info":
                case undefined:
                    return <InfoRegular className={classes.dialogIconInfo} />;
            }
        })();

        // Show a spinner until a main view has been set.
        if (!rootComponentService) {
            return (
                <ReactContextsWrapper contexts={contexts}>
                    <Theme className={classes.app} targetDocument={targetDocument}>
                        <Spinner className={classes.spinner} />
                    </Theme>
                </ReactContextsWrapper>
            );
        } else {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const Content: ComponentType = rootComponentService.rootComponent;

            return (
                <ReactContextsWrapper contexts={contexts}>
                    <Theme className={classes.app} targetDocument={targetDocument}>
                        <ToastProvider imperativeRef={setToastHandle}>
                            <Dialog open={!!requiredExtensions} modalType="alert">
                                <DialogSurface>
                                    <DialogBody>
                                        <DialogTitle>Required Extensions</DialogTitle>
                                        <DialogContent>
                                            Opening this URL requires the following extensions to be installed and enabled:
                                            <ul>
                                                {requiredExtensions?.map((name) => (
                                                    <li key={name}>{name}</li>
                                                ))}
                                            </ul>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button appearance="primary" onClick={onAcceptRequiredExtensions}>
                                                Install & Enable
                                            </Button>
                                            <Button appearance="secondary" onClick={onRejectRequiredExtensions}>
                                                No Thanks
                                            </Button>
                                        </DialogActions>
                                    </DialogBody>
                                </DialogSurface>
                            </Dialog>
                            <Dialog open={!!extensionInstallError} modalType="alert">
                                <DialogSurface>
                                    <DialogBody>
                                        <DialogTitle>
                                            <div className={classes.extensionErrorTitleDiv}>
                                                Extension Install Error
                                                <ErrorCircleRegular className={classes.extensionErrorIcon} />
                                            </div>
                                        </DialogTitle>
                                        <DialogContent>
                                            <List>
                                                <ListItem>
                                                    <Body1>{`Extension "${extensionInstallError?.extension.name}" failed to install and was removed.`}</Body1>
                                                </ListItem>
                                                <ListItem>
                                                    <Body1>{`${extensionInstallError?.error}`}</Body1>
                                                </ListItem>
                                            </List>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button appearance="primary" onClick={onAcknowledgedExtensionInstallError}>
                                                Close
                                            </Button>
                                        </DialogActions>
                                    </DialogBody>
                                </DialogSurface>
                            </Dialog>
                            <Dialog open={!!currentDialog} modalType="alert">
                                <DialogSurface>
                                    <DialogBody>
                                        <DialogTitle>
                                            <div className={classes.dialogTitle}>
                                                {currentDialogIcon}
                                                {currentDialog?.title}
                                            </div>
                                        </DialogTitle>
                                        {currentDialog?.content && <DialogContent>{currentDialog.content}</DialogContent>}
                                        <DialogActions>
                                            <Button appearance="primary" onClick={onDismissDialog}>
                                                OK
                                            </Button>
                                        </DialogActions>
                                    </DialogBody>
                                </DialogSurface>
                            </Dialog>
                            <Suspense fallback={<Spinner className={classes.spinner} />}>
                                <DialogContext.Provider value={{ showDialog }}>
                                    <Content />
                                </DialogContext.Provider>
                            </Suspense>
                        </ToastProvider>
                    </Theme>
                </ReactContextsWrapper>
            );
        }
    };

    // Derive the target document from the container element. When the container is in a popup
    // window (or other document distinct from the main one), this is what makes Fluent inject
    // styles and render portals into the correct document.
    const containerOwnerDocument = containerElement.ownerDocument;
    const targetDocument = containerOwnerDocument && containerOwnerDocument !== document ? containerOwnerDocument : undefined;

    // Set the container element to be a flex container so that the tool can be displayed properly.
    const originalContainerElementDisplay = containerElement.style.display;
    containerElement.style.display = "flex";

    // Create and render the react root component.
    const reactRoot = createRoot(containerElement);
    reactRoot.render(createElement(modularToolRootComponent));

    let disposed = false;
    return {
        // eslint-disable-next-line @typescript-eslint/promise-function-async
        dispose: () => {
            // Unmount and restore the original container element display.
            if (!disposed) {
                disposed = true;
                reactRoot.unmount();
                containerElement.style.display = originalContainerElementDisplay;
            }
            // The promise resolves once the React effect cleanup
            // (which disposes the ServiceContainer) has completed.
            return disposeDeferred.promise;
        },
    };
}
